import React from "react";

import copyToClipboard from "copy-to-clipboard";
import _ from "lodash";
import { Link } from "react-router-dom";

import {
  RemoveApplicantModalProvider,
  MoveApplicantModalProvider,
  EditIncomeModalProvider,
  RemoveApplicantModalV2,
  MoveApplicantModal,
  EditIncomeModal,
} from "manager/components/app-summary/modals";
import { useAppRow } from "manager/components/app-summary/use-app-row";
import { useIsViewOnlyUser } from "manager/hooks/useIsViewOnlyUser";
import Icon from "shared/components/Icon";
import { Popover } from "shared/components/Popover";
import Tooltip from "shared/components/Tooltip";
import {
  InfoIcon,
  MoveIcon,
  DeleteIcon,
  LinkOneIcon,
  UnblockIcon,
} from "shared/components/vega/icons";
import {
  useGetApplicantWorkflowRun,
  useListApplicantPayments,
} from "shared/hooks/api";
import { PAST_PAID_STATUS } from "shared/interfaces/types";
import { useVeroFlags } from "shared/utils/launchDarkly";

import "../styles.css";

interface ApplicantInfoCardProps {
  id: string;
  applicant: any;
  legacyApp: any;
  refetchDeal?: () => void;
  payments?: any[];
}

// Component for the progress bar
const ProgressBar = ({ percent }) => {
  const isComplete = percent === 100;

  return (
    <div
      className="app-summary-v2 vega progress-container"
      style={{ width: "100%" }}
    >
      <div className="vega progress-bar-wrapper" style={{ width: "100%" }}>
        <div
          className={`vega progress-bar-fill ${isComplete ? "success" : ""}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};

const ApplicantInfoCard: React.FC<ApplicantInfoCardProps> = ({
  id,
  applicant,
  legacyApp,
  refetchDeal,
}) => {
  const { isViewOnly } = useIsViewOnlyUser();
  const flags = useVeroFlags();
  const { applicantWorkflow } = useGetApplicantWorkflowRun(id);
  const { payments = [] } = useListApplicantPayments(id);
  const legacyAppId = applicant?.legacyId || legacyApp?.appId;
  const isSubmitted = legacyApp?.info?.isSubmitted;

  const { infoProps, onCopyAppLink, unblock, modalProps } = useAppRow({
    ...legacyApp,
    refetchDeal,
  });

  const disableMoveAndDelete =
    _.get(applicant, "isPrimaryApplication") &&
    _.get(legacyApp, "location")?.includes("yardi") &&
    !_.chain(payments)
      .filter((payment) => PAST_PAID_STATUS.includes(_.get(payment, "status")))
      .isEmpty()
      .value();

  const pastPaidMessage = disableMoveAndDelete
    ? "Action Restricted: The primary applicant cannot be changed or deleted after payment due to integration limitations with your PMS."
    : undefined;

  const partyType = applicant?.party
    ? _.capitalize(applicant.party.replace("-", " "))
    : "";
  const hasPayments = !_.isEmpty(payments);
  const hasPaid = _.some(payments, (payment) =>
    PAST_PAID_STATUS.includes(payment?.status)
  );

  const calculateProgress = () => {
    if ((!applicantWorkflow || !applicantWorkflow?.tasks) && !isSubmitted) {
      return { percent: 0, status: "Not Started" };
    }

    if (applicantWorkflow?.done || isSubmitted) {
      return { percent: 100, status: "Complete" };
    }

    const { complete = [], possible = [], next = [] } = applicantWorkflow.tasks;

    const completedTasks = complete.length;
    const totalTasks = completedTasks + possible.length + next.length;
    const percent = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

    let status;
    if (percent === 100) {
      status = "Complete";
    } else if (percent === 0) {
      status = "Not Started";
    } else {
      status = `${Math.round(percent)}%`;
    }

    return { percent, status };
  };

  const { percent, status: progressStatus } = calculateProgress();
  const debugInfo = _.join(
    [applicant?.application, applicant?.id, applicant?.person],
    "\n"
  );

  return (
    <div
      className="app-summary-v2 vega card applicant-info-card service"
      style={{
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        border: "1px solid rgba(0, 0, 0, 0.06)",
        borderRadius: "8px",
        transition: "all 0.2s ease-in-out",
        background: "#fff",
        position: "relative",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow =
          "0 4px 16px rgba(0, 0, 0, 0.15), 0 0 0 3px rgba(79, 70, 229, 0.3)";
        e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
        e.currentTarget.style.transform = "translateY(0) scale(1)";
      }}
    >
      {!isViewOnly && <RemoveApplicantModalV2 {...modalProps} />}
      {!isViewOnly && <MoveApplicantModal {...modalProps} />}
      <EditIncomeModal
        income={legacyApp?.income?.income}
        statedIncome={legacyApp?.income?.stated}
        applicantName={legacyApp?.info?.name}
        applicantId={legacyAppId}
      />

      {/* Main clickable area for navigation */}
      <Link
        to={`/applications/${legacyAppId}`}
        style={{
          textDecoration: "none",
          color: "inherit",
          cursor: "pointer",
          display: "block",
        }}
      >
        <div
          className="vega flex gap small column"
          style={{
            width: "100%",
            padding: "12px",
            paddingBottom: "60px",
          }}
        >
          {/* Applicant Name with Avatar */}
          <div className="vega flex start center gap small">
            <div className="vega avatar-circle">
              {applicant?.firstName?.charAt(0).toUpperCase()}
            </div>
            <div className="vega title">
              {applicant?.firstName} {applicant?.lastName}
            </div>
          </div>

          {/* Email - below avatar and name */}
          <a
            className="vega sub-title small email"
            href={`mailto:${applicant?.email}`}
            onClick={(e) => e.stopPropagation()}
            style={{
              wordBreak: "break-word",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%",
            }}
          >
            {applicant?.email}
          </a>

          {/* Application Status Progress Bar */}
          <div className="vega" style={{ width: "100%" }}>
            <div className="vega text small">
              <strong>Application Progress: </strong>
              {progressStatus}
            </div>
            <ProgressBar percent={percent} />
          </div>

          {/* Paid Status */}
          {hasPayments && (
            <div className="vega flex align-center">
              <div className="vega text small">
                <strong>Application Payment Status: </strong>

                {hasPaid ? (
                  <div className="vega align-center">
                    <Icon.CheckGreenSmall className="vega icon" />
                    <span className="vega text">Paid</span>
                  </div>
                ) : (
                  <div className="vega align-center">
                    <Icon.OrangeExclamationSmall className="vega icon" />
                    <span className="vega text">Not Paid</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Badge - Centered at bottom */}
        {partyType && (
          <div
            className="vega badge-container"
            style={{ marginBottom: "50px" }}
          >
            <span className="vega badge">{partyType}</span>
          </div>
        )}
      </Link>

      {/* Action Buttons - positioned absolutely to avoid link */}
      <div
        className="vega flex gap small justify-center actions"
        style={{
          position: "absolute",
          bottom: "12px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
        }}
      >
        {legacyApp?.info?.isBlocked && legacyApp?.info?.permission?.unblock && (
          <Tooltip title="Unblock applicant" theme="light">
            <UnblockIcon
              className="vega icon button"
              onClick={() =>
                legacyApp?.info?.permission?.unblock && unblock?.action()
              }
            />
          </Tooltip>
        )}

        {legacyApp?.info?.inviteLink && !flags.copyInviteLinkDisabled && (
          <Tooltip title="Copy invite link" theme="light">
            <LinkOneIcon
              role="button"
              aria-label="Copy invite link"
              className="vega icon button"
              onClick={onCopyAppLink}
            />
          </Tooltip>
        )}

        <Popover
          placement="topRight"
          content={
            <div className="vega content flex column no-gap">
              <pre className="vega">{debugInfo}</pre>
              <span
                className="vega button actions end"
                onClick={() => copyToClipboard(debugInfo)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" && copyToClipboard(debugInfo)
                }
              >
                copy
              </span>
            </div>
          }
        >
          <InfoIcon className="vega icon button" />
        </Popover>

        {!isViewOnly && (
          <Tooltip
            title={
              pastPaidMessage ||
              legacyApp?.info?.permission?.noMoveReason ||
              "Move to another application"
            }
            theme="light"
          >
            <MoveIcon
              className="vega icon button"
              onClick={() =>
                !disableMoveAndDelete &&
                !legacyApp?.info?.permission?.noMove &&
                infoProps?.onMove()
              }
            />
          </Tooltip>
        )}

        {!isViewOnly && (
          <Tooltip
            title={
              pastPaidMessage ||
              legacyApp?.info?.permission?.noRemoveReason ||
              "Remove from application"
            }
            theme="light"
          >
            <DeleteIcon
              className="vega icon button"
              onClick={() =>
                !disableMoveAndDelete &&
                !legacyApp?.info?.permission?.noRemove &&
                infoProps?.onDelete()
              }
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

const ApplicantInfoCardWithActions: React.FC<ApplicantInfoCardProps> = (
  props
) => {
  return (
    <RemoveApplicantModalProvider>
      <MoveApplicantModalProvider>
        <EditIncomeModalProvider>
          <ApplicantInfoCard {...props} />
        </EditIncomeModalProvider>
      </MoveApplicantModalProvider>
    </RemoveApplicantModalProvider>
  );
};

export default ApplicantInfoCardWithActions;
